/**
 * Network and performance simulation utilities
 */

import type { NetworkSimulationOptions } from './types'
import { getRandom } from '../factories/base'
import { applyDelay } from './delay'

/**
 * Simulate network conditions
 */
export async function simulateNetworkConditions(
  options: NetworkSimulationOptions
): Promise<void> {
  const { connection, packetLoss, latency, bandwidth } = options

  // Simulate packet loss
  if (packetLoss && getRandom() < packetLoss) {
    throw new Error('Packet loss simulated')
  }

  // Apply connection-based latency
  switch (connection) {
    case 'fast':
      await applyDelay(latency || [10, 50])
      break
    case 'slow':
      await applyDelay(latency || [1000, 3000])
      break
    case 'unstable':
      await applyDelay(latency || [100, 2000])
      break
    case 'offline':
      throw new Error('Network offline')
    default:
      await applyDelay(latency || [100, 300])
  }

  // Simulate bandwidth limitations (simplified)
  if (bandwidth) {
    const dataSize = 1024 // Assume 1KB response
    const transferTime = (dataSize / bandwidth) * 1000 // Convert to ms
    await applyDelay(transferTime)
  }
}

/**
 * Simulate server load
 */
export async function simulateServerLoad(
  loadFactor: number = 0.5,
  baseDelay: number = 100
): Promise<void> {
  // Higher load = longer delays
  const loadDelay = baseDelay * (1 + loadFactor * 2)
  await applyDelay([loadDelay * 0.8, loadDelay * 1.2])
}

/**
 * Simulate database query time
 */
export async function simulateDatabaseQuery(
  complexity: 'simple' | 'medium' | 'complex' = 'simple',
  recordCount: number = 100
): Promise<void> {
  let baseTime: number

  switch (complexity) {
    case 'simple':
      baseTime = 10
      break
    case 'medium':
      baseTime = 50
      break
    case 'complex':
      baseTime = 200
      break
    default:
      baseTime = 50
  }

  // Scale with record count (logarithmically)
  const scaledTime = baseTime * Math.log10(recordCount + 1)
  await applyDelay([scaledTime * 0.5, scaledTime * 1.5])
}

/**
 * Simulate file processing time
 */
export async function simulateFileProcessing(
  fileSize: number, // in bytes
  processingType: 'upload' | 'download' | 'transform' = 'upload'
): Promise<void> {
  const sizeInMB = fileSize / (1024 * 1024)
  let timePerMB: number

  switch (processingType) {
    case 'upload':
      timePerMB = 500 // 500ms per MB
      break
    case 'download':
      timePerMB = 200 // 200ms per MB
      break
    case 'transform':
      timePerMB = 1000 // 1s per MB
      break
    default:
      timePerMB = 500
  }

  const processingTime = sizeInMB * timePerMB
  await applyDelay([processingTime * 0.8, processingTime * 1.2])
}

/**
 * Simulate cache behavior
 */
export function simulateCache<T>(
  key: string,
  factory: () => T,
  ttl: number = 60000 // 1 minute default
): T {
  const cache = getCacheInstance()
  const cached = cache.get(key)

  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.value
  }

  const value = factory()
  cache.set(key, { value, timestamp: Date.now() })
  return value
}

/**
 * Simple in-memory cache
 */
const cacheStore = new Map<string, { value: any; timestamp: number }>()

function getCacheInstance() {
  return {
    get: (key: string) => cacheStore.get(key),
    set: (key: string, value: { value: any; timestamp: number }) => {
      cacheStore.set(key, value)
    },
    clear: () => cacheStore.clear(),
    delete: (key: string) => cacheStore.delete(key),
  }
}

/**
 * Simulate authentication check delay
 */
export async function simulateAuthCheck(
  tokenComplexity: 'simple' | 'jwt' | 'oauth' = 'jwt'
): Promise<void> {
  switch (tokenComplexity) {
    case 'simple':
      await applyDelay([5, 15])
      break
    case 'jwt':
      await applyDelay([20, 50])
      break
    case 'oauth':
      await applyDelay([100, 300])
      break
    default:
      await applyDelay([20, 50])
  }
}

/**
 * Simulate rate limiting
 */
export class RateLimiter {
  private requests = new Map<string, number[]>()

  constructor(
    private limit: number = 100,
    private windowMs: number = 60000 // 1 minute
  ) {}

  isAllowed(identifier: string): boolean {
    const now = Date.now()
    const windowStart = now - this.windowMs
    
    // Get existing requests for this identifier
    const requests = this.requests.get(identifier) || []
    
    // Filter out old requests
    const recentRequests = requests.filter(time => time > windowStart)
    
    // Check if limit exceeded
    if (recentRequests.length >= this.limit) {
      return false
    }
    
    // Add current request
    recentRequests.push(now)
    this.requests.set(identifier, recentRequests)
    
    return true
  }

  getRemainingRequests(identifier: string): number {
    const now = Date.now()
    const windowStart = now - this.windowMs
    const requests = this.requests.get(identifier) || []
    const recentRequests = requests.filter(time => time > windowStart)
    
    return Math.max(0, this.limit - recentRequests.length)
  }

  getResetTime(identifier: string): number {
    const requests = this.requests.get(identifier) || []
    if (requests.length === 0) {
      return Date.now()
    }
    
    return Math.min(...requests) + this.windowMs
  }
}

/**
 * Simulate circuit breaker pattern
 */
export class CircuitBreaker {
  private failures = 0
  private lastFailureTime = 0
  private state: 'closed' | 'open' | 'half-open' = 'closed'

  constructor(
    private failureThreshold: number = 5,
    private recoveryTimeMs: number = 60000 // 1 minute
  ) {}

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() - this.lastFailureTime > this.recoveryTimeMs) {
        this.state = 'half-open'
      } else {
        throw new Error('Circuit breaker is open')
      }
    }

    try {
      const result = await operation()
      this.onSuccess()
      return result
    } catch (error) {
      this.onFailure()
      throw error
    }
  }

  private onSuccess(): void {
    this.failures = 0
    this.state = 'closed'
  }

  private onFailure(): void {
    this.failures++
    this.lastFailureTime = Date.now()
    
    if (this.failures >= this.failureThreshold) {
      this.state = 'open'
    }
  }

  getState(): 'closed' | 'open' | 'half-open' {
    return this.state
  }
}

/**
 * Simulate progressive backoff
 */
export async function simulateBackoff(
  attempt: number,
  baseDelay: number = 1000,
  maxDelay: number = 30000,
  factor: number = 2
): Promise<void> {
  const delay = Math.min(baseDelay * Math.pow(factor, attempt - 1), maxDelay)
  const jitter = delay * 0.1 * getRandom() // Add 10% jitter
  
  await applyDelay(delay + jitter)
}

/**
 * Simulate health check
 */
export function simulateHealthCheck(): {
  status: 'healthy' | 'degraded' | 'unhealthy'
  checks: Record<string, boolean>
  timestamp: string
} {
  const checks = {
    database: getRandom() > 0.05, // 95% healthy
    cache: getRandom() > 0.02, // 98% healthy
    externalApi: getRandom() > 0.1, // 90% healthy
    storage: getRandom() > 0.01, // 99% healthy
  }

  const healthyCount = Object.values(checks).filter(Boolean).length
  const totalChecks = Object.keys(checks).length

  let status: 'healthy' | 'degraded' | 'unhealthy'
  if (healthyCount === totalChecks) {
    status = 'healthy'
  } else if (healthyCount >= totalChecks * 0.7) {
    status = 'degraded'
  } else {
    status = 'unhealthy'
  }

  return {
    status,
    checks,
    timestamp: new Date().toISOString(),
  }
}
